import { AbstractControl } from '@angular/forms';

/**
 * Utility class for centralizing re-useable validation logic.
 * @date 6/16/2022 - 5:44:42 PM
 *
 * @export
 * @class ValidationUtil
 * @typedef {ValidationUtil}
 */
export class ValidationUtil {
	/**
	 * Since there are so many different things a control can
	 * need validation wise we want to create hints for form
	 * fields that are intelligent & interestingly sequential.
	 *
	 * This utility will accept a form control and return the
	 * 'next most interesting hint' given the many things that
	 * may be wrong at the same time such as failing a regex,
	 * whether it is required or not, its min / max length, etc.
	 * @date 6/16/2022 - 5:58:36 PM
	 *
	 * @public
	 * @static
	 * @param {AbstractControl} control
	 * @returns {string}
	 */
	public static curateValidationHint(control: AbstractControl): string {
		//If there are no errors we have nothing to hint at
		//empty string will be falsey and work for the upstream
		//controls.
		if (!control.errors) {
			return '';
		}

		// Password-specific validations - checking these first as they're more specific
		if (control.errors['passwordLength']) {
			return 'Password must be at least 8 characters long.';
		}

		if (control.errors['passwordUpperCase']) {
			return 'Password must contain at least one uppercase letter.';
		}

		if (control.errors['passwordLowerCase']) {
			return 'Password must contain at least one lowercase letter.';
		}

		if (control.errors['passwordNumber']) {
			return 'Password must contain at least one number.';
		}

		if (control.errors['passwordSpecialChar']) {
			return 'Password must contain at least one special character.';
		}

		// Generic validations
		if (control.errors['required']) {
			return 'This field is required.';
		}

		if (control.errors['minlength']) {
			return 'This field is too short.';
		}

		if (control.errors['maxlength']) {
			return 'This field is too long.';
		}

		if (control.errors['invalid']) {
			return 'This field is missing required characters or contains invalid characters.';
		}

		if (control.errors['confirmValidator']) {
			return 'Passwords do not match.';
		}

		//Every error case should be accounted for above.

		//NOTE: This isn't a server side error - it's client side so this thrown
		//error does not denote a security risk. In the rare instance this happens
		//due to an unforseen error case we will need the control and its associated errors
		//listed in the console so we can update this function to handle that case.
		//I don't anticipate this ever happening but putting this here will save us one
		//build-round-trip of troubleshooting if it did.
		console.error(control.errors, control);
		throw new Error('Unknown validation case.');
	}
}
